import { logError } from '../../shared/logger';
import { postData, HttpError } from '../../shared/httpClient';
import { Redirect } from '../../shared/types/Redirect';
import { DeepLinkResult, FetchDeepLink } from '../../shared/types/FetchDeepLink';
import { buildBookingComFallbackUrl } from '../../shared/builders/buildBookingComFallbackUrl';

const DEEP_LINKS_SYSTEM_URL = '/mzn-deeplinks';

const parseGhostMode = (headers: Headers): boolean | undefined => {
  const value = headers.get('X-Ghost-Mode');
  return value !== null ? value === 'true' : undefined;
};

const requestDeepLink = async (url: string, redirect: Redirect): Promise<DeepLinkResult> => {
  const { body, headers } = await postData(url, redirect.payload);
  return { url: body, ghostMode: parseGhostMode(headers) };
};

export const fetchRentalCarsDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/rentalcarsad/`;

  const deepLink = await requestDeepLink(url, redirect);

  return deepLink;
};

export const fetchWlBookingComHotelsDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/wl-bookingcom-hotels/`;

  return requestDeepLink(url, redirect);
};

export const fetchBookingComCarsDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/bookingcom-cars/`;

  return requestDeepLink(url, redirect);
};

export const fetchBookingComDeepLink: FetchDeepLink = async (redirect) => {
  try {
    let target = 'desktop_bcom';
    if (redirect.campaign === 'bookingcom_mobile_checkbox') {
      target = 'mobile_bcom';
    } else if (redirect.campaign === 'bookingcom-omio-discounts-5p') {
      target = 'bookingcom-omio-discounts-5p';
    } else if (redirect.campaign === 'bookingcom-new-user') {
      target = 'bookingcom-new-user';
    }

    const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/${target}/`;

    return await requestDeepLink(url, redirect);
  } catch (error) {
    const message = (error && (error as Error)?.message) || 'Unknown';
    const httpStatus = error instanceof HttpError ? { http_status_i: error.status } : {};

    logError(`Fetching Booking.com deep link failed with error: ${message}`, { campaign: redirect.campaign, ...httpStatus });
  }

  return { url: buildBookingComFallbackUrl(redirect) };
};

export const fetchImpalaDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/impala/`;

  const deepLink = await requestDeepLink(url, redirect);

  return deepLink;
};

export const fetchExpediaDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/expedia/`;
  const deepLink = await requestDeepLink(url, redirect);

  return deepLink;
};

export const fetchKayakCarsDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/kayak-cars/`;

  const deepLink = await requestDeepLink(url, redirect);

  return deepLink;
};

export const fetchKayakStaysDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/kayak-stays/`;

  const deepLink = await requestDeepLink(url, redirect);

  return deepLink;
};

export const fetchVioDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/vio/`;

  const deepLink = await requestDeepLink(url, redirect);

  return deepLink;
};

export const fetchActivitiesDeepLink: FetchDeepLink = async (redirect) => {
  if (!redirect.payload.arrivalId) {
    throw new Error('Missing arrivalId');
  }

  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/gyg/`;

  const deepLink = await requestDeepLink(url, redirect);

  return deepLink;
};

export const fetchClickthroughDeepLink: FetchDeepLink = async (redirect) => {
  const url = `${redirect.origin}${DEEP_LINKS_SYSTEM_URL}/v2/clickthrough/`;

  const deepLink = await requestDeepLink(url, redirect);

  return deepLink;
};
